import classes from "./Bewertungen.module.css";
import { Modal } from "../UI/Modal";
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";

// Fake Bewertungen intentionally disabled.
// Keep this component connected to API/member data only.

const BewertungenModal = ({ open, onClose, children, totalCount = 0 }) => {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia && window.matchMedia('(max-width: 742px)').matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia('(max-width: 742px)');
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener ? mq.addEventListener('change', handler) : mq.addListener(handler);
    return () => {
      mq.removeEventListener ? mq.removeEventListener('change', handler) : mq.removeListener(handler);
    };
  }, []);

  if (!open) return null;

  if (!isMobile) {
    return (
      <Modal open={open} onBackdropClick={onClose}>
        <div className={classes.bewertungenModalHeader}>
          <div className={classes.bewertungenModalHeaderText}>
            <h3 className="titleSemibold3">Alle Bewertungen</h3>
            <span className="regularXl">({totalCount} Bewertungen)</span>
          </div>
          <button aria-label="Close" className={classes.iconButton} onClick={onClose}>
            <svg xmlns="http://www.w3.org/2000/svg" width="17" height="18" viewBox="0 0 17 18" fill="none">
              <path fillRule="evenodd" clipRule="evenodd" d="M7.50947 9L0 1.49054L0.990535 0.5L8.5 8.00947L16.0095 0.5L17 1.49054L9.49054 9L17 16.5095L16.0095 17.5L8.5 9.99054L0.990535 17.5L0 16.5095L7.50947 9Z" fill="#1F1F1F"/>
            </svg>
          </button>
        </div>
        <div className={classes.bewertungenModalContent}>
          {children}
        </div>
      </Modal>
    );
  }

  const bottomSheet = (
    <>
      <div className={classes.bewertungenBottomSheetBackdrop} onClick={onClose} />
      <div className={`${classes.bewertungenBottomSheet} ${classes.slideUp}`}>
        <header className={classes.bottomSheetHeader}>
          <h3 className="titleSemibold3">Alle Bewertungen</h3>
          <button aria-label="Close" className={classes.iconButton} onClick={onClose}>
            <svg xmlns="http://www.w3.org/2000/svg" width="17" height="18" viewBox="0 0 17 18" fill="none">
              <path fillRule="evenodd" clipRule="evenodd" d="M7.50947 9L0 1.49054L0.990535 0.5L8.5 8.00947L16.0095 0.5L17 1.49054L9.49054 9L17 16.5095L16.0095 17.5L8.5 9.99054L0.990535 17.5L0 16.5095L7.50947 9Z" fill="#1F1F1F"/>
            </svg>
          </button>
        </header>
        <div className={classes.bottomSheetContent}>
          {children}
        </div>
      </div>
    </>
  );

  return createPortal(bottomSheet, document.body);
};

const BewertungItem = ({ bewertung }) => {

  const renderStars = (rating) => {
    const starArray = [];
    for (let i = 0; i < rating; i++) {
        starArray.push(
            <svg
                key={i}
                xmlns="http://www.w3.org/2000/svg"
                width="10"
                height="9"
                viewBox="0 0 10 9"
                fill="none"
                className={classes.redStar}
            >
                <g clipPath="url(#clip0_3947_4431)">
                    <path
                        d="M7.76885 6.70095L8.15793 8.9889L5.22363 7.44914L7.60133 5.73047L7.71482 6.38649L7.76885 6.70095Z"
                        fill="#DF315A"
                    />
                    <path
                        d="M2.73343 6.38783L2.84691 5.7318L0.474609 3.42758L3.75476 2.95048L5.22461 -0.00976562L6.69446 2.95048L9.97461 3.42758L2.29031 8.99023L2.73343 6.38783Z"
                        fill="#DF315A"
                    />
                </g>
                <defs>
                    <clipPath id="clip0_3947_4431">
                        <rect
                            width="9.5"
                            height="9"
                            fill="white"
                            transform="translate(0.474609 -0.00976562)"
                        />
                    </clipPath>
                </defs>
            </svg>
        );
    }
    return starArray;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 7) {
        return `vor ${diffDays} Tagen`;
    } else if (diffDays < 30) {
        const weeks = Math.floor(diffDays / 7);
        return `vor ${weeks} ${weeks === 1 ? 'Woche' : 'Wochen'}`;
    } else {
        const months = Math.floor(diffDays / 30);
        return `vor ${months} ${months === 1 ? 'Monat' : 'Monaten'}`;
    }
  };

  return (
    <div className={classes.bewertungenItem}>
      <header className={classes.bewertungenItemHeader}>
        <div className={classes.bewertungenItemHeaderImage}>
          <img src={bewertung.image} alt={bewertung.name} />
        </div>
        <div className={classes.bewertungenItemHeaderText}>
          <p className="semiboldMiddle">{bewertung.name}</p>
          <div className={classes.bewertungenItemHeaderTextInfos}>
            <div className={classes.starsCountContainer}>  
              {renderStars(bewertung.stars)}
            </div>
            <span className="semiboldXs">{formatDate(bewertung.when)}</span>
          </div>
        </div>
      </header>
      <p className="regularSmall">
        {bewertung.review}
      </p>
    </div>
  )
}

export const Bewertungen = ({ creative }) => {
  const [isOpen, setIsOpen] = useState(false);
  const reviews = useMemo(() => {
    const rawReviews = creative?.reviews?.items || [];
    if (!Array.isArray(rawReviews)) return [];

    return rawReviews
      .filter((item) => item && (item.review || item.comment))
      .map((item, index) => ({
        id: item.id || item._id || `review-${index}`,
        when: item.when || item.createdAt || item.createdAtDate || new Date().toISOString(),
        name:
          item.name ||
          item.userName ||
          item.authorName ||
          item.user?.displayName ||
          "Stu Nutzer",
        stars: Math.max(
          1,
          Math.min(
            5,
            Math.round(
              Number(item.stars ?? item.rating ?? item.score ?? 0) || 1
            )
          )
        ),
        review: item.review || item.comment || "",
        image:
          item.image ||
          item.profilePicture ||
          item.user?.processedProfilePicture ||
          item.user?.profilePicture ||
          "",
      }));
  }, [creative?.reviews?.items]);

  const visibleBewertungen = useMemo(() => reviews.slice(0, 3), [reviews]);
  const restBewertungen = useMemo(() => reviews.slice(3), [reviews]);
  const hasReviews = reviews.length > 0;

  const handleOpen = () => {
    if (restBewertungen.length === 0) return;
    setIsOpen(true);
  };
  const handleClose = () => setIsOpen(false);

  return (
      <div className={classes.bewertungen}>
        {hasReviews ? (
        <>
          <header className={classes.bewertungenHeader}>
            <h2 className="titleSemibold3">
              Bewertungen uber {creative?.displayName || "dieses Mitglied"}
            </h2>
            <svg className={classes.mobileSeparator} style={{margin: "0 6px"}} xmlns="http://www.w3.org/2000/svg" width="3" height="3" viewBox="0 0 3 3" fill="none">
              <circle cx="1.5" cy="1.5" r="1.5" fill="#1F1F1F"/>
            </svg>
            <div className={classes.starsContainer}>
              <span className="regularXl">4,5</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="19" height="18" viewBox="0 0 19 18" fill="none">
                <g clipPath="url(#clip0_15763_35225)">
                <path d="M13.8651 12.7155L14.6046 17.05L9.02734 14.1329L13.5467 10.877L13.7624 12.1198L13.8651 12.7155Z" fill="#1F1F1F"/>
                <path d="M4.29339 12.1201L4.50908 10.8773L0 6.51198L6.23466 5.60811L9.02844 0L11.8222 5.60811L18.0569 6.51198L3.45114 17.0503L4.29339 12.1201Z" fill="#1F1F1F"/>
                </g>
                <defs>
                <clipPath id="clip0_15763_35225">
                <rect width="18.0569" height="17.0503" fill="white"/>
                </clipPath>
                </defs>
              </svg>
              <span className={`${classes.ratingsNumberPhone} regularXl`}>({reviews.length} Bewertungen)</span>
            </div>
            <span className={`${classes.ratingsNumber} regularXl`}>({reviews.length} Bewertungen)</span>
          </header>
          <div className={classes.bewertungenContent}>
            {visibleBewertungen.map((bewertung) => (
              <BewertungItem key={bewertung.id} bewertung={bewertung} />
            ))}
          </div>
         
          {restBewertungen.length >= 3 && (
            <button onClick={handleOpen} className={`${classes.button} semiboldSmall `}>
              Alle {reviews.length} Bewertungen lesen
            </button>
          )}

          <BewertungenModal open={isOpen} onClose={handleClose} totalCount={reviews.length}>
            <div className={classes.bewertungenListAll}>
              {restBewertungen.map(bewertung => (
                <BewertungItem key={bewertung.id} bewertung={bewertung} />
              ))}
            </div>
          </BewertungenModal>
        </>
        ) : (
          <div className={classes.emptyState}>
            <span className="regularMiddle">
              Noch keine Bewertungen vorhanden.
            </span>
          </div>
        )}
      </div>
  );
};

export default Bewertungen;